import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import FAQDetailStyles from './FAQDetail.styled';
import ReactMarkdown from 'react-markdown';
import ReactDOMServer from 'react-dom/server';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import { getFAQ } from '../../services/faqService';
import Content from '../../components/Content/Content';
import Heading from '../../components/Heading/Heading';
import FAQSearch from '../../components/FAQSearch/FAQSearch';
import FAQMenu from '../../components/FAQMenu/FAQMenu';
import { getFaqs } from '../../services/faqs';
import { slugByLabel } from '../../util/faqCategories';
import FeaturedMedia from '../../components/Shared/FeaturedMedia';
import LoanApplicationPackage from '../../components/Shared/LoanApplicationPackage';
import linkRenderer from '../../util/renderers/link';
import useGTM from '../../hooks/useGTM';
import Error from '../../components/Error/Error';
import CTACardv2 from '../../components/CTACardv2/CTACardv2';
import theme from '../../styles/theme';

const parseSuggestions = suggestions =>
  suggestions.map(({ faqQuestion: label, slug: value, faqCategory }) => ({
    label,
    value: value.replace(
      'resources/faqs/',
      `resources/faqs/${slugByLabel(faqCategory?.[0])}/`
    ),
  }));

function FAQDetail({
  error,
  data: {
    faqQuestion,
    titleTag,
    slug,
    metaDescription,
    faqCategory,
    faqSummaryAnswer,
    faqAnswerContent1,
    faqAnswerVideos,
    faqAnswerContent2,
    faqTopThree,
  } = {},
  suggestions: initialSuggestions = [],
  isLoading,
  history,
  className,
  match,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();
  const [searchError, setSearchError] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSuggestions(parseSuggestions(initialSuggestions));
  }, [isLoading]);

  const handleChange = ({ value, currentTarget: { form } }) => {
    if (value.indexOf('/resources/faqs/') !== -1) {
      history.push(value);
      return;
    }

    setSearch(value);

    if (value.length >= 3 || !value) {
      const submit = form.querySelector('button[type="submit"]');

      setTimeout(() => {
        submit.click();
      }, 100);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(
      setTimeout(async () => {
        triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'FAQs',
          category: 'frequently_asked',
          action: 'search',
          label: 'faqs_search',
        })();

        setIsSearching(true);
        const _suggestions = await getFaqs('all', false, search);

        if (_suggestions.status !== 200 && _suggestions.status !== 204) {
          setSuggestions([]);
          setSearchError(_suggestions.statusText);
        } else {
          if (_suggestions.data.result) {
            setSuggestions(parseSuggestions(_suggestions.data.result));
            setSearchError(false);
          } else {
            setSuggestions([]);
            setSearchError('No results.');
          }
        }

        setIsSearching(false);
      }, 300)
    );
  };

  return (
    <Layout isLoading={isLoading} className={className}>
      <SEO
        title={titleTag}
        description={metaDescription}
        structuredData={`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [{
              "@type": "Question",
              "name": "${faqQuestion}",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "${ReactDOMServer.renderToString(
                  <ReactMarkdown
                    source={faqSummaryAnswer}
                    renderers={{
                      link: ({ href, children }) => (
                        <a
                          target='_blank'
                          href={`${
                            href[0] === '/' ? 'www.vmf.com' : ''
                          }${href.replace(':newtab', '')}`}
                          rel='noopener noreferrer'
                        >
                          {children}
                        </a>
                      ),
                    }}
                  />
                ).replace(/"/g, '\\"')}"
              }
            }]
          }
        `}
      />

      <Heading
        isAnimated={false}
        appearance='dark'
        background={theme.colors.blue10}
      >
        <h1>Frequently Asked Questions</h1>
        <FAQSearch
          search={search}
          suggestions={suggestions}
          onChange={handleChange}
          onSubmit={handleSubmit}
          isSearching={isSearching}
          error={searchError}
          errorInDropdown
        />
      </Heading>

      <FAQMenu>
        <div className='faq-content'>
          {!faqSummaryAnswer &&
            !faqAnswerContent1 &&
            !faqAnswerVideos &&
            !faqAnswerContent2 && (
              <Content divider={false}>
                <p>This FAQ has no content.</p>
              </Content>
            )}

          {!faqAnswerContent1 && faqSummaryAnswer && (
            <Content divider={false}>
              <h2 className='type-h3'>{faqQuestion}</h2>
              <ReactMarkdown
                source={faqSummaryAnswer}
                renderers={{
                  link: linkRenderer,
                }}
              />
            </Content>
          )}

          {faqAnswerContent1 && (
            <Content divider={false}>
              <h2 className='type-h3'>{faqQuestion}</h2>
              <ReactMarkdown
                source={faqAnswerContent1}
                renderers={{
                  link: linkRenderer,
                }}
              />
            </Content>
          )}

          {faqAnswerVideos &&
            faqAnswerVideos.length > 0 &&
            faqAnswerVideos.map(faqAnswerVideo => (
              <FeaturedMedia
                alt={faqAnswerVideo?.media?.fields?.description}
                src={
                  faqAnswerVideo?.media?.fields?.file?.url ||
                  faqAnswerVideo?.thumbnail?.fields?.file?.url
                }
                mediaType={faqAnswerVideo?.mediaType}
                video={{
                  source: faqAnswerVideo?.videoSource,
                  content: faqAnswerVideo?.embeddedContent,
                }}
                autoplay={false}
              />
            ))}

          {faqAnswerContent2 && (
            <Content divider={false}>
              <ReactMarkdown
                source={faqAnswerContent2}
                renderers={{
                  link: linkRenderer,
                }}
              />
            </Content>
          )}
        </div>

        {(match.params.questionId === 'how-do-i-buy-a-home' ||
          match.params.questionId === 'how-do-i-sell-my-home') && (
          <>
            <LoanApplicationPackage
              type={
                match.params.questionId === 'how-do-i-buy-a-home'
                  ? 'buy'
                  : 'sell'
              }
            />
          </>
        )}
      </FAQMenu>

      <CTACardv2
        preHeaderContent={'Have Questions?'}
        headerContent={`We're Here to Help`}
        buttonText={'Contact Us'}
        buttonUrl={'/contact-us/'}
        gtmClassName={'contact'}
        ctaIntro={
          <>
            <p>
              Existing customers <a href='tel:1-800-970-7250'>1-800-970-7250</a>
            </p>
            <p>
              New customers <a href='tel:1-866-701-0467'>1-866-701-0467</a>
            </p>
          </>
        }
      />
    </Layout>
  );
}

FAQDetail.getInitialProps = async ({ match, req }) => {
  try {
    // Fetch server-rendered data here
    const { questionId } = match.params;
    const suggestions = await getFaqs('all', false);
    const faqDetail = await getFAQ(`/resources/faqs/${questionId}/`);

    if (suggestions.status !== 200 && suggestions.status !== 204) {
      throw suggestions.statusText;
    }

    if (faqDetail.status !== 200 && faqDetail.status !== 204) {
      throw faqDetail.statusText;
    }

    return {
      suggestions: suggestions.data.result,
      data: faqDetail.data.result,
    };
  } catch (error) {
    return { error };
  }
};

FAQDetail.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default FAQDetailStyles(FAQDetail);
